import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import SiteHeader from "../components/SiteHeader";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import {
  LayoutDashboard, Users, CreditCard, Building2, TrendingUp,
  UserCheck, PoundSterling, FileSearch, Search,
} from "lucide-react";

const gbp = (n) => `£${Number(n || 0).toLocaleString("en-GB", { maximumFractionDigits: 0 })}`;
const fmtDate = (s) => (s ? new Date(s).toLocaleString() : "—");

const STATUS_BADGE = {
  paid:      "bg-emerald-100 text-emerald-900 border-emerald-500",
  pending:   "bg-amber-100 text-amber-900 border-amber-500",
  failed:    "bg-red-100 text-red-900 border-red-500",
  initiated: "bg-zinc-100 text-zinc-700 border-zinc-400",
};

export default function AdminPortal() {
  const { user, ready } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState(null);
  const [payments, setPayments] = useState(null);
  const [workspaces, setWorkspaces] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [userQuery, setUserQuery] = useState("");
  const [payQuery, setPayQuery] = useState("");

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    (async () => {
      try {
        const [s, u, p, w] = await Promise.all([
          api.get("/admin/stats"),
          api.get("/admin/users"),
          api.get("/admin/payments"),
          api.get("/admin/workspaces"),
        ]);
        setStats(s.data); setUsers(u.data); setPayments(p.data); setWorkspaces(w.data);
      } catch (err) {
        const msg = err?.response?.data?.detail || err?.message || "Failed to load admin data";
        setLoadError(String(msg));
        setStats((v) => v || {}); setUsers((v) => v || []); setPayments((v) => v || []); setWorkspaces((v) => v || []);
      }
    })();
  }, [user]);

  if (!ready) return null;
  if (!user || user.role !== "admin") return <Navigate to="/app" replace />;

  const filteredUsers = (users || []).filter((u) => {
    const q = userQuery.trim().toLowerCase();
    if (!q) return true;
    return (u.email || "").toLowerCase().includes(q) ||
           (u.name || "").toLowerCase().includes(q) ||
           (u.workspace_name || "").toLowerCase().includes(q);
  });
  const filteredPayments = (payments || []).filter((p) => {
    const q = payQuery.trim().toLowerCase();
    if (!q) return true;
    return (p.user_email || "").toLowerCase().includes(q) ||
           (p.contact_email || "").toLowerCase().includes(q) ||
           (p.contact_name || "").toLowerCase().includes(q) ||
           (p.session_id || "").toLowerCase().includes(q) ||
           (p.payment_status || "").toLowerCase().includes(q) ||
           (p.provider || "").toLowerCase().includes(q);
  });

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <SiteHeader />
      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Admin · Portal</div>
        <h1 className="font-heading text-4xl font-bold tracking-tight mt-2 flex items-center gap-3">
          <LayoutDashboard className="w-7 h-7 text-[#0F172A]" /> Business overview
        </h1>

        {loadError && (
          <div className="mt-6 border border-red-500 bg-red-50 text-red-900 px-4 py-3 rounded-sm font-mono text-xs" data-testid="admin-load-error">
            Failed to load admin data — {loadError}
          </div>
        )}

        {/* Stat cards */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard icon={PoundSterling} label="Revenue · all time" value={gbp(stats?.revenue?.all_time)}
                    sub={`${stats?.revenue?.paid_count || 0} paid txns`} testid="stat-revenue-total" />
          <StatCard icon={TrendingUp} label="Revenue · 30d" value={gbp(stats?.revenue?.last_30d)}
                    sub={`£${(stats?.revenue?.last_7d || 0).toFixed(0)} in 7d`} testid="stat-revenue-30d" />
          <StatCard icon={FileSearch} label="Checks · all time" value={stats?.totals?.checks ?? "—"}
                    sub={`${stats?.activity?.checks_7d || 0} in 7d`} testid="stat-checks" />
          <StatCard icon={UserCheck} label="Expert reviews" value={stats?.totals?.reviews ?? "—"}
                    sub={`${stats?.totals?.pending_reviews || 0} pending`} testid="stat-reviews" />
          <StatCard icon={Users} label="Users" value={stats?.totals?.users ?? "—"} testid="stat-users" />
          <StatCard icon={Building2} label="Workspaces" value={stats?.totals?.workspaces ?? "—"} testid="stat-workspaces" />
          <StatCard icon={CreditCard} label="Stripe revenue" value={gbp(stats?.revenue?.by_provider?.stripe)} testid="stat-stripe" />
          <StatCard icon={CreditCard} label="Razorpay revenue" value={gbp(stats?.revenue?.by_provider?.razorpay)} testid="stat-razorpay" />
        </div>

        {/* Tabs */}
        <Tabs defaultValue="users" className="mt-10">
          <TabsList className="rounded-sm bg-zinc-100 p-1">
            <TabsTrigger value="users" className="rounded-sm data-[state=active]:bg-white" data-testid="portal-tab-users">Clients ({users?.length || 0})</TabsTrigger>
            <TabsTrigger value="payments" className="rounded-sm data-[state=active]:bg-white" data-testid="portal-tab-payments">Payments ({payments?.length || 0})</TabsTrigger>
            <TabsTrigger value="workspaces" className="rounded-sm data-[state=active]:bg-white" data-testid="portal-tab-workspaces">Workspaces ({workspaces?.length || 0})</TabsTrigger>
          </TabsList>

          <TabsContent value="users" className="mt-4">
            <div className="mb-3 relative max-w-sm">
              <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input value={userQuery} onChange={(e) => setUserQuery(e.target.value)}
                     placeholder="Search by name / email / firm…"
                     className="rounded-sm h-9 pl-9" data-testid="portal-users-search" />
            </div>
            <div className="border border-zinc-200 bg-white rounded-sm overflow-hidden">
              <table className="w-full text-sm" data-testid="portal-users-table">
                <thead className="bg-zinc-50 border-b border-zinc-200 text-left">
                  <tr>
                    <TH>Name / Email</TH>
                    <TH>Workspace</TH>
                    <TH>Role</TH>
                    <TH className="text-right">Credits</TH>
                    <TH className="text-right">Checks</TH>
                    <TH className="text-right">Reviews</TH>
                    <TH className="text-right">Spend</TH>
                    <TH>Joined</TH>
                  </tr>
                </thead>
                <tbody>
                  {users === null ? (
                    <tr><td colSpan={8} className="p-6 text-center text-zinc-500">Loading…</td></tr>
                  ) : filteredUsers.length === 0 ? (
                    <tr><td colSpan={8} className="p-6 text-center text-zinc-500">No matches</td></tr>
                  ) : filteredUsers.map((u) => (
                    <tr key={u.id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50">
                      <td className="px-4 py-3">
                        <div className="font-medium">{u.name || <span className="text-zinc-400">—</span>}</div>
                        <div className="font-mono text-xs text-zinc-500">{u.email}</div>
                      </td>
                      <td className="px-4 py-3 text-sm">{u.workspace_name || <span className="text-zinc-400">—</span>}</td>
                      <td className="px-4 py-3">
                        {u.role === "admin" ? (
                          <span className="font-mono text-[10px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded-sm bg-[#0F172A] text-white">Admin</span>
                        ) : u.workspace_role ? (
                          <span className="font-mono text-[10px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded-sm bg-zinc-100 text-zinc-700 border border-zinc-300">{u.workspace_role}</span>
                        ) : (
                          <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-400">solo</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-sm">{u.review_credits}</td>
                      <td className="px-4 py-3 text-right font-mono text-sm">{u.checks_count}</td>
                      <td className="px-4 py-3 text-right font-mono text-sm">{u.reviews_count}</td>
                      <td className="px-4 py-3 text-right font-mono text-sm font-bold">{gbp(u.total_spend)}</td>
                      <td className="px-4 py-3 font-mono text-xs text-zinc-500">{fmtDate(u.created_at).split(",")[0]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="payments" className="mt-4">
            <div className="mb-3 relative max-w-sm">
              <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input value={payQuery} onChange={(e) => setPayQuery(e.target.value)}
                     placeholder="Search by email / status / session…"
                     className="rounded-sm h-9 pl-9" data-testid="portal-payments-search" />
            </div>
            <div className="border border-zinc-200 bg-white rounded-sm overflow-hidden">
              <table className="w-full text-sm" data-testid="portal-payments-table">
                <thead className="bg-zinc-50 border-b border-zinc-200 text-left">
                  <tr>
                    <TH>Client</TH>
                    <TH>Provider</TH>
                    <TH>Package</TH>
                    <TH className="text-right">Amount</TH>
                    <TH>Status</TH>
                    <TH>Session</TH>
                    <TH>Created</TH>
                  </tr>
                </thead>
                <tbody>
                  {payments === null ? (
                    <tr><td colSpan={7} className="p-6 text-center text-zinc-500">Loading…</td></tr>
                  ) : filteredPayments.length === 0 ? (
                    <tr><td colSpan={7} className="p-6 text-center text-zinc-500">No payment records yet</td></tr>
                  ) : filteredPayments.map((p) => (
                    <tr key={p.id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50">
                      <td className="px-4 py-3">
                        <div className="text-sm">{p.contact_name || p.user_email || <span className="text-zinc-400">—</span>}</div>
                        <div className="font-mono text-xs text-zinc-500">{p.contact_email || p.user_email}</div>
                      </td>
                      <td className="px-4 py-3 capitalize text-sm">{p.provider || "stripe"}</td>
                      <td className="px-4 py-3 font-mono text-xs">{p.package_id}</td>
                      <td className="px-4 py-3 text-right font-mono text-sm font-bold">{gbp(p.amount)} <span className="text-zinc-500 uppercase text-[10px]">{p.currency}</span></td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-sm border font-mono text-[10px] uppercase tracking-[0.15em] ${STATUS_BADGE[p.payment_status] || STATUS_BADGE.initiated}`}>
                          {p.payment_status}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-zinc-500 truncate max-w-[160px]" title={p.session_id}>{p.session_id?.slice(0, 22)}…</td>
                      <td className="px-4 py-3 font-mono text-xs text-zinc-500">{fmtDate(p.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="workspaces" className="mt-4">
            <div className="border border-zinc-200 bg-white rounded-sm overflow-hidden">
              <table className="w-full text-sm" data-testid="portal-workspaces-table">
                <thead className="bg-zinc-50 border-b border-zinc-200 text-left">
                  <tr>
                    <TH>Firm</TH>
                    <TH className="text-right">Members</TH>
                    <TH className="text-right">Shared credits</TH>
                    <TH>Branded</TH>
                    <TH>Created</TH>
                  </tr>
                </thead>
                <tbody>
                  {workspaces === null ? (
                    <tr><td colSpan={5} className="p-6 text-center text-zinc-500">Loading…</td></tr>
                  ) : workspaces.length === 0 ? (
                    <tr><td colSpan={5} className="p-6 text-center text-zinc-500">No workspaces yet</td></tr>
                  ) : workspaces.map((w) => (
                    <tr key={w.id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50">
                      <td className="px-4 py-3 font-medium">{w.name}</td>
                      <td className="px-4 py-3 text-right font-mono text-sm">{w.member_count}</td>
                      <td className="px-4 py-3 text-right font-mono text-sm">{w.review_credits}</td>
                      <td className="px-4 py-3">
                        {w.has_logo ? (
                          <span className="font-mono text-[10px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded-sm bg-emerald-100 text-emerald-900 border border-emerald-500">Yes</span>
                        ) : (
                          <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-400">No</span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-zinc-500">{fmtDate(w.created_at).split(",")[0]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>

        <div className="mt-8 flex flex-wrap gap-3 text-sm">
          <Link to="/app/admin/reviews" className="text-[#0F172A] underline underline-offset-4" data-testid="portal-open-inbox">
            → Open expert-review inbox
          </Link>
        </div>
      </main>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="border border-zinc-200 bg-white rounded-sm p-4" data-testid={testid}>
      <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="font-heading text-3xl font-bold tracking-tight mt-2">{value}</div>
      {sub && <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function TH({ children, className = "" }) {
  return (
    <th className={`px-4 py-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 ${className}`}>
      {children}
    </th>
  );
}
